import type { GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { SSRConfig } from 'next-i18next';
import { useTranslation } from 'next-i18next';
import get from 'lodash/get';
import { AxiosResponseHeaders } from 'axios';
import { requestWithCache } from '../core/request';
import markdownComponents from '../components/common/markdownComponents';
import ContentWrapper from '../components/common/ContentWrapper';
import type FooterType from '../../types/Footer';
import translator from '../core/helpers/translator';
import { Locale } from '../core/constants';
import Markdown from '../components/common/Markdown/Markdown';
import { generateLinks } from '../core/helpers/link';
import PageLayout from '../components/common/PageLayout';
import HotelCount from '../../types/HotelCount';

type PrivacyPolicy = Partial<{
  title: string;
  content: string;
  updated_at: string;
}>;

type Props = {
  title: string;
  content: string;
  updated_at: string;
  locale: string;
  footer: FooterType | undefined;
  hotelCount: HotelCount | number | undefined;
} & Metadata &
  SSRConfig;

const GuestPolicyPage: NextPage<Props> = ({
  locale,
  title,
  content,
  updated_at,
  footer,
  hotelCount,
}) => {
  const { t } = useTranslation();

  return (
    <PageLayout
      footer={{
        content: footer,
      }}
      hotelCount={hotelCount}
    >
      <main className="grow bg-gray-50">
        <ContentWrapper className="bg-white pb-20 pt-12 sm:pb-36 sm:pt-16">
          <h1 id="privacy-policy-title" className="mb-4 text-title3 font-bold">
            {title}
          </h1>
          <p className="mb-4 text-small" data-testid="privacy-policy-date">
            {t('page:terms_of_service.last_updated')}
            {new Date(updated_at).toLocaleString(locale === 'ja' ? 'ja-JP' : 'en-US', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </p>
          <Markdown components={markdownComponents}>{content}</Markdown>
          <div className="mt-12 flex justify-end sm:mt-16">
            <a href="#guest-policy-title">
              <p className="border-b border-orange-700 text-small text-orange-700">
                {t('common:back_to_top')}
              </p>
            </a>
          </div>
        </ContentWrapper>
      </main>
    </PageLayout>
  );
};

const revalidate = process.env.REVALIDATE_TERMS_PAGES
  ? Number(process.env.REVALIDATE_TERMS_PAGES)
  : false;

export const getStaticProps: GetStaticProps<
  Props,
  {
    hotel: string;
  }
> = async ({ locale }) => {
  const ssrConfig = await serverSideTranslations(translator.locale.get(locale), [
    'common',
    'component',
    'page',
  ]);

  const privacyPolicyPromise = await requestWithCache<PrivacyPolicy>({
    url: `${
      process.env.NEXT_PUBLIC_BFF_URL
    }/terms-and-conditions/privacy-policy?locale=${translator.locale.get(locale)}&tabist=ok`,
    method: 'GET',
  });

  const footerPromise = await requestWithCache<FooterType>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/footer?locale=${translator.locale.get(
      locale
    )}&tabist=ok`,
    method: 'GET',
  });

  const hotelCountPromise = requestWithCache<AxiosResponseHeaders & { 'x-total-count': number }>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/hotels?locale=${Locale.JAPANESE}&tabist=ok`,
    method: 'HEAD',
  });

  const [privacyPolicy, footer, hotelCount] = await Promise.all([
    privacyPolicyPromise,
    footerPromise,
    hotelCountPromise,
  ]);

  const namespaces =
    // eslint-disable-next-line no-underscore-dangle
    locale && ssrConfig._nextI18Next.initialI18nStore[translator.locale.get(locale)];
  const url = new URL(`${process.env.NEXT_PUBLIC_SITE_URL}/privacy-policy`);

  const translationTargets = {
    title: get(namespaces.page, 'privacy_policy.title', null),
    description: get(namespaces.page, 'meta.description', null),
  };

  const translatedMetadata = await translator.batch.translate(translationTargets, locale);

  const metadata = {
    title: translatedMetadata.title,
    description: translatedMetadata.description,
    'og:locale': locale,
    'og:url': `${url.origin}${locale !== Locale.JAPANESE ? `/${locale}` : ''}${url.pathname}`,
  };

  return {
    props: {
      ...ssrConfig,
      title: privacyPolicy.title || '',
      content: privacyPolicy.content || '',
      updated_at: privacyPolicy.updated_at || '',
      locale: locale || Locale.JAPANESE,
      footer,
      hotelCount: hotelCount['x-total-count'] && Number(hotelCount['x-total-count']),
      metadata: locale && namespaces && metadata,
      links: generateLinks(url),
    },
    revalidate,
  };
};

export default GuestPolicyPage;
